import { supabase } from '../supabaseClient';

export const ALL_SYSTEM_IDS = [
  'water-billing',
  'property-tax',
  'e-logbook',
  'business-permit',
  'rural-health',
  'civil-registry',
  'settings'
];

export const systemAccessService = {
  /**
   * Fetches allowed system IDs for a user based on their role and department assignments.
   * @param {Object} currentUser - User object containing role, department, etc.
   * @returns {Promise<string[]>} Array of system IDs allowed for the user.
   */
  async getAllowedSystemsForUser(currentUser) {
    if (!currentUser) return [];

    const role = String(currentUser?.role || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/-/g, '');

    // Superadmin has full access to all systems
    if (role === 'superadmin') {
      return ALL_SYSTEM_IDS;
    }

    // Check if user has explicit allowed_systems array assigned
    if (Array.isArray(currentUser.allowed_systems)) {
      return currentUser.allowed_systems;
    }

    if (!currentUser.department) {
      return role === 'admin' ? ['settings'] : [];
    }

    try {
      // Lookup department by department_name
      const { data: deptData, error: deptError } = await supabase
        .from('lgu_departments')
        .select('id')
        .eq('department_name', currentUser.department)
        .eq('is_active', true)
        .single();

      if (deptError || !deptData) {
        console.error('Department lookup failed:', deptError);
        return role === 'admin' ? ['settings'] : [];
      }

      // Fetch assigned systems from department_systems table
      const { data: systemsData, error: systemsError } = await supabase
        .from('department_systems')
        .select('system_id')
        .eq('department_id', deptData.id);

      if (systemsError) {
        console.error('Error fetching department_systems:', systemsError);
        return role === 'admin' ? ['settings'] : [];
      }

      let systemIds = (systemsData || []).map(s => s.system_id);

      // Accounting department restriction for BPLO
      if (currentUser.department?.toLowerCase().includes('accounting')) {
        systemIds = systemIds.filter(id => id !== 'business-permit');
      }

      // Include settings system for admins if not present
      if (role === 'admin' && !systemIds.includes('settings')) {
        systemIds.push('settings');
      }

      return systemIds;
    } catch (error) {
      console.error('Error calculating allowed systems:', error);
      return role === 'admin' ? ['settings'] : [];
    }
  }
};
